import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  AutorenewRounded as ReopenIcon,
  BuildCircleOutlined as RefaireIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  FactCheckOutlined as SuivisIcon,
  HelpOutline as InconnueIcon,
  MoreVert as MoreIcon,
  OpenInNew as FicheIcon,
  PauseCircleOutline as AttenteIcon,
  PlayCircleOutline as StartIcon,
  PlaylistAdd as SaisirIcon,
  PrintOutlined as PrintIcon,
  Search as SearchIcon,
  TaskAlt as DoneIcon,
  TimelapseRounded as CoursIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import { GlassSurface } from '../../../components/GlassDashboard';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { auditLogsAPI, comptoirMatieresAPI, comptoirProductionAPI, comptoirServicesAPI } from '../../../services/api';
import { useComptoirSuivi, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import { ComptoirEtape, ComptoirEtapeAction, ComptoirSaisieType, ComptoirSuiviService } from '../../../types/comptoir';
import { EtapeDialog, EtapeMotifDialog, ProdBar, SaisieDialog, StatutChip, fq, slotLabel } from '../components/ProductionDialogs';
import { COLOR, EmptyState, ETAT_TONE, StateChip, fDate, num, primaryBtnSx } from '../ui';
import PlansButton from '../../../components/plans/PlansButton';
import TravauxModifiesAlert from '../components/TravauxModifiesAlert';

const MINI = 'Suivi de production';
type Filtre = '' | 'cours' | 'attente' | 'refaire' | 'saisir';
const FILTRES: [Filtre, string][] = [
  ['', 'Tous les projets'], ['cours', 'Étapes en cours'], ['attente', 'Étapes en attente'], ['refaire', 'Pièces à refaire'], ['saisir', 'Situations à saisir'],
];
const ORANGE = '#ED6C02';

/** yyyy-mm-dd in local time (toISOString would shift the day after midnight UTC). */
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Monday → Sunday of the week of `ref`, shifted by `n` weeks. */
const week = (n = 0, ref = new Date()) => {
  const mon = new Date(ref);
  mon.setDate(ref.getDate() - ((ref.getDay() + 6) % 7) + n * 7);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { du: isoDay(mon), au: isoDay(sun) };
};

const matches = (s: ComptoirSuiviService, f: Filtre, q: string) => {
  if (f === 'attente' && !s.nb_attente) return false;
  if (f === 'cours' && !(s.nb_en_cours + s.nb_partielles)) return false;
  if (f === 'saisir' && !s.nb_inconnues) return false;
  if (f === 'refaire' && !num(s.a_refaire)) return false;
  if (!q) return true;
  const hay = [s.reference, s.titre, s.client_nom, ...s.etapes.map((e) =>
    [e.designation, e.travail_label, e.machine?.code, e.machine?.name, e.responsable?.full_name].join(' '))].join(' ').toLowerCase();
  return hay.includes(q);
};

/** Suivi de production: the étapes of the comptoir services, tracked in pieces. */
const ComptoirProduction: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('Comptoir', 'add', MINI);
  const canEdit = hasPermission('Comptoir', 'edit', MINI);
  const canDelete = hasPermission('Comptoir', 'delete', MINI);
  const canSeeService = hasPermission('Comptoir', 'view', 'Services & Projets');
  const invalidate = useInvalidateComptoir();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  // V8 « Fiche de travail » for the atelier: travaux, matière, étapes, visa boxes
  const ficheTravail = async (s: ComptoirSuiviService) => {
    try {
      const sv = await comptoirServicesAPI.get(s.id);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf'))
          .getFicheTravailPDFBlob(sv, s, await comptoirMatieresAPI.list(s.id).catch(() => [])),
        fileName: `Travail_${s.reference}.pdf`,
      });
      auditLogsAPI.logPdfDownload('ComptoirService', s.id, `Aperçu fiche de travail - ${s.reference}`);
    } catch (err: any) { showError(extractErrorMessage(err)); }
  };
  const q = useComptoirSuivi();
  const d = q.data;

  const [filtre, setFiltre] = useState<Filtre>('');
  const [search, setSearch] = useState('');
  const only = params.get('service') ? Number(params.get('service')) : null;

  const [etapeDlg, setEtapeDlg] = useState<{ etape: ComptoirEtape | null; serviceId: number | null } | null>(null);
  const [saisie, setSaisie] = useState<{ id: number; kind?: ComptoirSaisieType; reference: string; plans?: number } | null>(null);
  const [motifDlg, setMotifDlg] = useState<{ etape: ComptoirEtape; mode: 'attente' | 'terminer' } | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printRange, setPrintRange] = useState(week());
  const [printing, setPrinting] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; e: ComptoirEtape; s: ComptoirSuiviService; i: number } | null>(null);

  useEffect(() => {
    // "Ajouter les étapes" from a service page: /comptoir/production?service=12&nouvelle=1
    if (params.get('nouvelle') && only && canAdd) {
      setEtapeDlg({ etape: null, serviceId: only });
      params.delete('nouvelle');
      setParams(params, { replace: true });
    }
  }, [params, only, canAdd, setParams]);

  const term = search.trim().toLowerCase();
  const rows = useMemo(
    () => (d?.services || []).filter((s) => (!only || s.id === only) && matches(s, filtre, term)),
    [d, filtre, term, only],
  );
  const sans = (d?.sans_etapes || []).filter((s) => !only || s.id === only);
  const onlyRef = only ? (d?.services.find((s) => s.id === only) || d?.sans_etapes.find((s) => s.id === only))?.reference : null;

  /* ---------- actions ---------- */
  const act = async (e: ComptoirEtape, action: ComptoirEtapeAction) => {
    try {
      const r = await comptoirProductionAPI.statut(e.id, action);
      invalidate();
      showSuccess(`${r.designation} : ${r.statut_label}`);
    } catch (err: any) { showError(extractErrorMessage(err)); }
  };
  const terminer = (e: ComptoirEtape) => {
    if (e.defined && num(e.conformes) < num(e.demandee)) {
      if (!canEdit) {
        showError(`Enregistrez d'abord la quantité réalisée : ${fq(e.conformes)}/${fq(e.demandee)} conformes.`);
        return;
      }
      setMotifDlg({ etape: e, mode: 'terminer' });
      return;
    }
    act(e, 'terminer');
  };
  const move = async (e: ComptoirEtape, sens: 'haut' | 'bas') => {
    try { await comptoirProductionAPI.moveEtape(e.id, sens); invalidate(); } catch (err: any) { showError(extractErrorMessage(err)); }
  };
  const remove = (e: ComptoirEtape) => {
    showConfirm(`Supprimer l'étape « ${e.designation} » ? Les étapes suivantes seront renumérotées.`, async () => {
      try { await comptoirProductionAPI.deleteEtape(e.id); invalidate(); showSuccess('Étape supprimée'); } catch (err: any) { showError(extractErrorMessage(err)); }
    }, { title: "Supprimer l'étape", confirmText: 'Supprimer' });
  };

  /* ---------- printed sheet: the étapes planned in a range, by day ---------- */
  const printErr = !printRange.du || !printRange.au ? 'Choisissez les deux dates.'
    : printRange.au < printRange.du ? 'La date de fin est avant la date de début.' : '';
  const print = async () => {
    if (printErr) return;
    setPrinting(true);
    try {
      const data = await comptoirProductionAPI.planning(printRange.du, printRange.au);
      setPrintOpen(false);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getSuiviProductionPDFBlob(data),
        fileName: `Suivi_production_${printRange.du}_${printRange.au}.pdf`,
      });
    } catch (err: any) { showError(extractErrorMessage(err)); } finally { setPrinting(false); }
  };

  /* ---------- KPIs ---------- */
  const k = d?.kpis;
  const kpis: { f: Filtre; title: string; value: React.ReactNode; sub: string; color: string; icon: React.ReactNode }[] = [
    { f: '', title: 'Projets suivis', value: k?.projets, sub: 'avec des étapes de fabrication', color: '#1E88E5', icon: <SuivisIcon /> },
    { f: 'cours', title: 'Étapes en cours', value: k?.en_cours, sub: `dont partiellement réalisées : ${k?.partielles ?? 0}`, color: '#1E88E5', icon: <CoursIcon /> },
    { f: 'attente', title: 'En attente', value: k?.attente, sub: 'arrêtées, avec motif', color: '#D32F2F', icon: <AttenteIcon /> },
    { f: 'refaire', title: 'Pièces à refaire', value: k ? fq(k.a_refaire) : undefined, sub: 'non conformes à reprendre', color: ORANGE, icon: <RefaireIcon /> },
    { f: 'saisir', title: 'Situations à saisir', value: k?.inconnues, sub: 'étapes commencées avant le suivi', color: ORANGE, icon: <InconnueIcon /> },
  ];

  const canSaisir = (e: ComptoirEtape, s: ComptoirSuiviService) =>
    canAdd && !s.cloture && (e.statut !== 'termine' || num(e.a_refaire) > 0)
    && !(s.be_bloque && e.statut === 'a_faire');  // V8: the production starts after the plan validation

  return (
    <Box>
      {/* ---------- header ---------- */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ maxWidth: 820 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Suivi de production</Typography>
          <Typography variant="body2" color="text.secondary">
            Pour chaque étape : quantité <b>demandée</b>, <b>réalisée</b>, <b>conforme</b> (bonne pour l'étape suivante), <b>à refaire</b> et <b>restante</b>.
            Chaque saisie s'ajoute à l'historique, rien n'est écrasé. Une étape ne peut pas utiliser plus de pièces que l'étape précédente n'en a produit de conformes.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => { setPrintRange(week()); setPrintOpen(true); }}
            sx={{ borderColor: COLOR, color: COLOR }}>
            Imprimer
          </Button>
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEtapeDlg({ etape: null, serviceId: only })} sx={primaryBtnSx}>
              Nouvelle étape
            </Button>
          )}
        </Stack>
      </Stack>

      {/* ---------- KPIs (also filters) ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {kpis.map((x) => {
          const on = filtre === x.f && x.f !== '';
          return (
            <Grid item xs={12} sm={6} md key={x.title}>
              <GlassSurface accentColor={x.color}
                sx={{ p: 2, height: '100%', cursor: 'pointer', outline: on ? `2px solid ${x.color}` : 'none', transition: 'outline .15s' }}
                onClick={() => setFiltre(on ? '' : x.f)}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">{x.title}</Typography>
                    {d ? <Typography variant="h5" sx={{ fontWeight: 700 }}>{x.value}</Typography> : <Skeleton width={60} height={36} />}
                    <Typography variant="caption" color="text.secondary">{x.sub}</Typography>
                  </Box>
                  <Box sx={{ color: x.color, '& svg': { fontSize: 34 } }}>{x.icon}</Box>
                </Stack>
              </GlassSurface>
            </Grid>
          );
        })}
      </Grid>

      {/* ---------- filters ---------- */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField size="small" placeholder="Réf., client, étape, machine, responsable" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} aria-label="Effacer la recherche"><ClearIcon fontSize="small" /></IconButton></InputAdornment>
              ),
            }} />
          <TextField select size="small" value={filtre} onChange={(e) => setFiltre(e.target.value as Filtre)} sx={{ minWidth: 220 }} aria-label="Filtre"
            SelectProps={{ displayEmpty: true }}>
            {FILTRES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          {only && (
            <Chip label={`Uniquement ${onlyRef || 'ce service'}`} onDelete={() => { params.delete('service'); setParams(params, { replace: true }); }}
              sx={{ fontWeight: 600, bgcolor: alpha(COLOR, 0.1), color: COLOR }} />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{d ? `${rows.length} projet(s)` : ''}</Typography>
        </Stack>
      </Paper>
      {q.isFetching && <LinearProgress sx={{ height: 2, mb: 1, '& .MuiLinearProgress-bar': { bgcolor: COLOR }, bgcolor: alpha(COLOR, 0.1) }} />}

      {!d && <Skeleton variant="rounded" height={220} />}
      {d && rows.length === 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <EmptyState icon={<SuivisIcon />} compact
            title={d.services.length ? 'Aucun projet ne correspond au filtre' : 'Aucun projet en production avec des étapes'}
            text={d.services.length ? undefined : 'Ajoutez les étapes de fabrication d’un service (découpe, pliage…) pour suivre ses pièces.'} />
        </Paper>
      )}

      {/* ---------- one card per service ---------- */}
      {rows.map((s) => (
        <Paper key={s.id} sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5}
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(COLOR, 0.04) }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' }}>{s.reference}</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap title={s.titre}>{s.titre}</Typography>
              <StateChip label={s.etat_com_label || s.etat_label} t={ETAT_TONE[s.etat_com || s.etat]} small />
              {s.en_retard && <Chip size="small" label="En retard" color="error" sx={{ height: 22, fontWeight: 700 }} />}
              {s.be_bloque && <Chip size="small" label="Plan non validé" color="warning" title={s.be_bloque} sx={{ height: 22, fontWeight: 700 }} />}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {s.client_nom || '—'} · deadline {fDate(s.deadline)}
              </Typography>
              {canAdd && !s.cloture && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEtapeDlg({ etape: null, serviceId: s.id })} sx={{ color: COLOR, whiteSpace: 'nowrap' }}>Étape</Button>
              )}
              <Tooltip title="Fiche de travail (atelier)">
                <IconButton size="small" onClick={() => ficheTravail(s)} sx={{ color: COLOR }} aria-label="Fiche de travail"><PrintIcon fontSize="small" /></IconButton>
              </Tooltip>
              {canSeeService && (
                <Button size="small" variant="outlined" startIcon={<FicheIcon />} onClick={() => navigate(`/comptoir/services/${s.id}`)}
                  sx={{ borderColor: COLOR, color: COLOR, whiteSpace: 'nowrap' }}>Fiche</Button>
              )}
            </Stack>
          </Stack>
          <TravauxModifiesAlert revoir={s.revoir} sx={{ borderRadius: 0 }}
            onVu={canEdit ? async () => { await comptoirProductionAPI.travauxVus(s.id); invalidate(); } : undefined} />
          <TableContainer sx={getModuleTableTheme('comptoir')}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell width={36}>#</TableCell><TableCell>Étape</TableCell><TableCell>Machine · responsable</TableCell>
                  <TableCell align="right">Demandée</TableCell><TableCell align="right">Réalisée</TableCell><TableCell align="right">Conformes</TableCell>
                  <TableCell align="right">À refaire</TableCell><TableCell align="right">Restante</TableCell>
                  <TableCell>Avancement</TableCell><TableCell>État</TableCell><TableCell align="right" width={250} />
                </TableRow>
              </TableHead>
              <TableBody>
                {s.etapes.map((e, i) => {
                  const u = e.inconnue;
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{e.ordre}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.designation}</Typography>
                        {e.travail_label && <Typography variant="caption" color="text.secondary" display="block">{e.travail_label}</Typography>}
                        <Typography variant="caption" color={e.date ? 'text.secondary' : 'warning.main'} display="block">{slotLabel(e)}</Typography>
                      </TableCell>
                      <TableCell>
                        {e.machine && (
                          <Box component="span" sx={{
                            fontFamily: 'monospace', fontSize: 12, fontWeight: 700, px: 0.75, py: 0.25, mr: 0.75, borderRadius: 1,
                            bgcolor: alpha(e.machine.disponible ? COLOR : '#D32F2F', 0.1), color: e.machine.disponible ? COLOR : '#D32F2F',
                          }} title={`${e.machine.name}${e.machine.disponible ? '' : ` — ${e.machine.status_label}`}`}>{e.machine.code}</Box>
                        )}
                        <Typography variant="body2" component="span">{e.responsable?.full_name || 'Non affecté'}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{e.defined ? fq(e.demandee) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{u ? '?' : fq(e.realisee)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{u ? '?' : fq(e.conformes)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: num(e.a_refaire) ? ORANGE : undefined, fontWeight: num(e.a_refaire) ? 700 : 400 }}>
                        {num(e.a_refaire) ? fq(e.a_refaire) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{u || !e.defined ? '?' : fq(e.restante)}</TableCell>
                      <TableCell><ProdBar e={e} compact /></TableCell>
                      <TableCell>
                        <StatutChip e={e} />
                        {e.attente?.motif && (
                          <Typography variant="caption" color="error" display="block" sx={{ maxWidth: 200 }}>
                            {e.attente.motif}{e.attente.detail ? ` — ${e.attente.detail}` : ''}
                          </Typography>
                        )}
                        {e.bloquee_par && (
                          <Typography variant="caption" color="text.secondary" display="block">Attend l'étape {e.bloquee_par.ordre}</Typography>
                        )}
                        {e.fin_forcee_motif && <Typography variant="caption" color="warning.main" display="block">Fin forcée : {e.fin_forcee_motif}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box component="span" sx={{ mr: 1 }}>
                          <PlansButton count={e.plans} title={`${s.reference} · ${e.ordre}. ${e.designation}`}
                            load={() => comptoirProductionAPI.etapePlans(e.id)} color={COLOR} />
                        </Box>
                        {canSaisir(e, s) && (
                          <Button size="small" variant="outlined" startIcon={<SaisirIcon />}
                            onClick={() => setSaisie({ id: e.id, reference: s.reference, plans: e.plans, kind: u ? 'initial' : e.statut === 'termine' ? 'reprise' : undefined })}
                            sx={{ borderColor: COLOR, color: COLOR }}>
                            Saisir
                          </Button>
                        )}
                        {(canAdd || canEdit || canDelete) && !s.cloture && (
                          <IconButton size="small" onClick={(ev) => setMenu({ anchor: ev.currentTarget, e, s, i })} aria-label={`Actions sur ${e.designation}`}>
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption">{s.nb_terminees}/{s.etapes.length} étape(s) terminée(s)</Typography>
            {s.nb_en_cours > 0 && <Typography variant="caption">{s.nb_en_cours} en cours</Typography>}
            {s.nb_partielles > 0 && <Typography variant="caption">{s.nb_partielles} partiellement réalisée(s)</Typography>}
            {s.nb_attente > 0 && <Typography variant="caption" color="error">{s.nb_attente} en attente</Typography>}
            {s.nb_inconnues > 0 && <Typography variant="caption" color="warning.main">{s.nb_inconnues} situation(s) à saisir</Typography>}
            {num(s.a_refaire) > 0 && <Typography variant="caption" color="warning.main">{fq(s.a_refaire)} pièce(s) à refaire</Typography>}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Dernière mise à jour : {s.derniere_maj
                ? `${new Date(s.derniere_maj.le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · ${s.derniere_maj.par || ''}`
                : '—'}
            </Typography>
          </Stack>
        </Paper>
      ))}

      {/* ---------- services without étape ---------- */}
      {d && sans.length > 0 && !filtre && !term && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Services sans étapes</Typography>
            <Typography variant="body2" color="text.secondary">{sans.length}</Typography>
          </Stack>
          {sans.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && <Divider />}
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, minWidth: 100 }}>{s.reference}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{s.titre}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.client_nom || '—'} · deadline {fDate(s.deadline)} · {s.etat_label}</Typography>
                </Box>
                {canAdd && (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setEtapeDlg({ etape: null, serviceId: s.id })}
                    sx={{ color: COLOR, whiteSpace: 'nowrap' }}>Ajouter les étapes</Button>
                )}
              </Stack>
            </React.Fragment>
          ))}
        </Paper>
      )}

      {/* ---------- row menu ---------- */}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && (() => {
          const { e, s, i } = menu;
          const close = (fn: () => void) => () => { setMenu(null); fn(); };
          const items: React.ReactNode[] = [];
          if (canEdit) items.push(<MenuItem key="edit" onClick={close(() => setEtapeDlg({ etape: e, serviceId: s.id }))}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Modifier / planifier</ListItemText></MenuItem>);
          if (canAdd && e.statut === 'a_faire') {
            items.push(
              <Tooltip key="start" title={e.bloquee_par ? `En attente de l'étape ${e.bloquee_par.ordre} « ${e.bloquee_par.designation} »` : ''} placement="left">
                <span>
                  <MenuItem disabled={!!e.bloquee_par} onClick={close(() => act(e, 'demarrer'))}>
                    <ListItemIcon><StartIcon fontSize="small" /></ListItemIcon><ListItemText>Démarrer</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>,
            );
          }
          if (canAdd && (e.statut === 'bloque' || e.statut === 'partiel')) items.push(<MenuItem key="resume" onClick={close(() => act(e, 'reprendre'))}><ListItemIcon><StartIcon fontSize="small" /></ListItemIcon><ListItemText>Reprendre le travail</ListItemText></MenuItem>);
          if (canAdd && ['a_faire', 'en_cours', 'partiel'].includes(e.statut)) items.push(<MenuItem key="wait" onClick={close(() => setMotifDlg({ etape: e, mode: 'attente' }))}><ListItemIcon><AttenteIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Mettre en attente</ListItemText></MenuItem>);
          if (canAdd && e.statut !== 'termine' && e.statut !== 'a_faire') items.push(<MenuItem key="end" onClick={close(() => terminer(e))}><ListItemIcon><DoneIcon fontSize="small" color="success" /></ListItemIcon><ListItemText>Terminer</ListItemText></MenuItem>);
          if (canEdit && e.statut === 'termine') items.push(<MenuItem key="reopen" onClick={close(() => act(e, 'rouvrir'))}><ListItemIcon><ReopenIcon fontSize="small" /></ListItemIcon><ListItemText>Rouvrir</ListItemText></MenuItem>);
          if (canEdit && e.nb_saisies > 0) items.push(<MenuItem key="corr" onClick={close(() => setSaisie({ id: e.id, reference: s.reference, plans: e.plans, kind: 'correction' }))}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Corriger une quantité</ListItemText></MenuItem>);
          if (canEdit && s.etapes.length > 1) {
            items.push(<Divider key="d1" />);
            if (i > 0) items.push(<MenuItem key="up" onClick={close(() => move(e, 'haut'))}><ListItemIcon><UpIcon fontSize="small" /></ListItemIcon><ListItemText>Monter</ListItemText></MenuItem>);
            if (i < s.etapes.length - 1) items.push(<MenuItem key="down" onClick={close(() => move(e, 'bas'))}><ListItemIcon><DownIcon fontSize="small" /></ListItemIcon><ListItemText>Descendre</ListItemText></MenuItem>);
          }
          if (canDelete && e.statut === 'a_faire' && !e.nb_saisies) {
            items.push(<Divider key="d2" />);
            items.push(<MenuItem key="del" onClick={close(() => remove(e))} sx={{ color: 'error.main' }}><ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Supprimer</ListItemText></MenuItem>);
          }
          return items.length ? items : [<MenuItem key="none" disabled>Aucune action</MenuItem>];
        })()}
      </Menu>

      {/* ---------- dialogs ---------- */}
      <EtapeDialog open={!!etapeDlg} etape={etapeDlg?.etape || null} serviceId={etapeDlg?.serviceId || null} canEdit={canEdit}
        onClose={() => setEtapeDlg(null)}
        onSaved={(e, created) => { setEtapeDlg(null); invalidate(); showSuccess(created ? `Étape ${e.ordre} ajoutée : ${e.designation}` : 'Étape enregistrée'); }} />
      <SaisieDialog open={!!saisie} etapeId={saisie?.id || null} kind={saisie?.kind} reference={saisie?.reference} plans={saisie?.plans} canEdit={canEdit}
        onClose={() => setSaisie(null)}
        onSaved={(e) => {
          setSaisie(null); invalidate();
          showSuccess(`${e.designation} : ${fq(e.conformes)}/${fq(e.demandee)} conformes${e.statut === 'termine' ? ' · étape terminée' : ''}`);
        }} />
      <EtapeMotifDialog open={!!motifDlg} etape={motifDlg?.etape || null} mode={motifDlg?.mode || 'attente'}
        onClose={() => setMotifDlg(null)}
        onSaved={(e) => { setMotifDlg(null); invalidate(); showSuccess(`${e.designation} : ${e.statut_label}`); }} />
      <Dialog open={printOpen} onClose={() => !printing && setPrintOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Imprimer le suivi de production</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Les étapes planifiées sur la période, jour par jour, avec leurs quantités et leur état.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {([['Semaine dernière', -1], ['Cette semaine', 0], ['Semaine prochaine', 1]] as [string, number][]).map(([l, n]) => {
              const w = week(n);
              const on = w.du === printRange.du && w.au === printRange.au;
              return (
                <Chip key={l} label={l} clickable onClick={() => setPrintRange(w)} variant={on ? 'filled' : 'outlined'}
                  sx={on ? { bgcolor: COLOR, color: '#fff' } : undefined} />
              );
            })}
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Du" type="date" fullWidth InputLabelProps={{ shrink: true }} value={printRange.du}
              onChange={(e) => setPrintRange({ ...printRange, du: e.target.value })} />
            <TextField label="Au" type="date" fullWidth InputLabelProps={{ shrink: true }} value={printRange.au}
              onChange={(e) => setPrintRange({ ...printRange, au: e.target.value })} error={!!printErr} />
          </Stack>
          {printErr && <Typography variant="caption" color="error">{printErr}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setPrintOpen(false)} disabled={printing}>Annuler</Button>
          <Button variant="contained" onClick={print} disabled={printing || !!printErr} sx={primaryBtnSx}
            startIcon={printing ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}>Imprimer</Button>
        </DialogActions>
      </Dialog>
      {pdfPreviewElement}
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
    </Box>
  );
};

export default ComptoirProduction;
